import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {getDomainLogoExtFromServer} from 'Api/Calendar';
import {getPersonalLogo, patchPersonalLogo, patchPersonalLogoList, uploadFileToZFS} from 'Api/ZCalendar';
import {HttpError} from 'Error/HttpError';
import {base64ToFile} from 'Utils';

export const LOGO_EMPTY = ''; // If a logo was set and deleted

const initialState = {
  additionalLogoList: [],
  logo: '',
  logoUrl: '',
  isLoading: false,
  isLoaded: false,
  logoEnforceUse: false,
};

export const getDomainLogo = createAsyncThunk(
  'logo/getDomainLogo',
  async (thunkAPI) => {
    try {
      const response = await getPersonalLogo();
      // Gmail / Outlook only support embedding public image url, base64 is not supported
      // so if not using logo url, need to upload current decoded base64 data to ZFS to get url
      if (response.value.from === 'user' && response.value.data && !response.value.url) {
        const uploadResult = await uploadFileToZFS(base64ToFile(response.value.data));
        await patchPersonalLogo({
          id: 'logo',
          value: {
            url: uploadResult.url,
          },
        });
        return {
          ...response,
          value: {
            ...response.value,
            url: uploadResult.url,
          },
        };
      }
      return response;
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.errorCode === 404) {
          return ({
            id: 'logo',
            value: {
              data: '',
            },
          });
        }
      }
      throw e;
    }
  }
);

export const getDomainLogoExt = createAsyncThunk(
  'logo/getDomainLogoExt',
  async (calId, thunkAPI) => {
    try {
      const response = await getDomainLogoExtFromServer(calId);
      return response;
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.errorCode === 404) {
          return ({
            id: 'logo',
            value: {
              data: '',
            },
          });
        }
      }
      throw e;
    }
  }
);

export const putPersonalLogo = createAsyncThunk(
  'logo/patchPersonalLogo',
  async (value, thunkAPI) => {
    const response = await patchPersonalLogo({
      id: 'logo',
      value,
    });
    return response;
  }
);

export const putPersonalLogoList = createAsyncThunk(
  'logo/patchPersonalLogoList',
  async (logoList, thunkAPI) => {
    const response = await patchPersonalLogoList({
      id: 'logoList',
      value: logoList,
    });
    return response;
  }
);

/**
 * Server will store logo data as a base64 string, not a data url. Converts given b64 string to data url for usage
 * @param {?string} data
 * @return {string} logo b64 data url or empty string
 */
export const makeDataUrl = (data) => {
  if (data) {
    if (data.startsWith('data:image')) {
      return data;
    }
    return 'data:image/webp;base64,' + data;
  } else {
    return LOGO_EMPTY;
  }
};

const onDomainLogoFulfilled = (state, action) => {
  const logoInfo = action.payload?.value;
  if (logoInfo) {
    state.logoEnforceUse = logoInfo.logoEnforceUse ?? false;
    // SVG attacks can cause browser crash, we do not support SVG logo
    const isSvg = logoInfo.data?.startsWith('data:image/svg+xml');
    const useUserLogo = !state.logoEnforceUse && logoInfo.from === 'user';
    const useAccountLogo = state.logoEnforceUse && logoInfo.from === 'admin';
    if (useUserLogo || useAccountLogo) {
      if (!isSvg) {
        state.logo = makeDataUrl(logoInfo.data);
      }
      state.logoUrl = logoInfo.url || '';
      state.additionalLogoList = logoInfo.logoList || [];
    } else {
      state.logo = '';
    }
  } else {
    state.logo = '';
  }
  state.isLoading = false;
  state.isLoaded = true;
};

const onDomainLogoExtRejected = (state) => {
  state.logo = LOGO_EMPTY;
  state.isLoaded = true;
};

export const logoState = createSlice({
  name: 'Logo',
  initialState,
  reducers: {
    setDomainLogoExt(state, action) {
      if (action.payload.error) {
        onDomainLogoExtRejected(state);
      } else {
        onDomainLogoFulfilled(state, action);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDomainLogo.fulfilled, onDomainLogoFulfilled);
    builder.addCase(getDomainLogo.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(getDomainLogo.rejected, (state, action) => {
      state.logo = LOGO_EMPTY;
      state.isLoading = false;
      state.isLoaded = true;
    });

    builder.addCase(getDomainLogoExt.fulfilled, onDomainLogoFulfilled);
    builder.addCase(getDomainLogoExt.rejected, onDomainLogoExtRejected);

    builder.addCase(putPersonalLogo.fulfilled, (state, action) => {
      state.isLoading = false;
      state.logo = makeDataUrl(action.payload?.value?.data);
      state.logoUrl = (action.payload?.value?.url) || '';
    });
    builder.addCase(putPersonalLogo.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(putPersonalLogo.rejected, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(putPersonalLogoList.fulfilled, (state, action) => {
      if (action.payload?.value) {
        state.additionalLogoList = action.payload.value;
      }
    });
  },
});

export const {
  setDomainLogoExt,
} = logoState.actions;

export default logoState.reducer;
